import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  Input,
  MenuItem,
  IconButton,
  Grid,
  Paper,
  Button,
  Select,
  FormControl,
  InputLabel
} from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import {useSelector} from 'react-redux'
import moment from 'moment';
import 'moment/locale/es';
import {getBookingSlots} from '../../../api'
import {SelctAppointmentLocaiton} from '../../SelctAppointmentLocaiton';
import {convertLocationToPosition} from '../../../utils/convertLocationToPosition';
import {useAggregatedServices} from '../../../utils/aggregateServices';
import {isPointInPolygon} from '../../../utils/isPointInPolygon';
// Set Spanish as the default locale for moment
moment.locale('es');

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: '800px',
    margin: '0 auto',
    flex:1,
    display:'flex',
    flexDirection:'column'
  },
  activitySelect: {
    width: '100%',
    marginBottom: theme.spacing(3),
  },
  dateCarousel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  dateCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
    '&.disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'inherit',
      },
    },
  },
  selectedDate: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  timeSlot: {
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
  },
  selectedTime: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  navigationButton: {
    padding: theme.spacing(1),
    '&.disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  actionButton: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 4),
    width: '100%',
    maxWidth: '300px',
    '&.disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
}));

const SelectDate = ({ onSelect, shortHash }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const activities = useSelector((state) => state.general.supplier?.activities)
  const address = useSelector((state) => state.general.supplier?.personalData?.address)
  const cities = useSelector((state) => state.general.supplier?.personalData?.cities)
  const allowMultipleServices = useSelector((state) => state.general.supplier?.personalData?.allowMultipleServices)
  const [selectedServices, setSelectedServices] = useState(() => activities?.length === 1 ? [activities[0]]: []);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [visibleDates, setVisibleDates] = useState([]);
  const [availableHours, setAvailableHours] = useState({});
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [customerAddress, setCustomerAddress] = React.useState(null);
  const aggregatedActivities = useAggregatedServices(selectedServices)

const isLocationValid = useMemo(()=> isPointInPolygon(customerAddress,cities),[customerAddress,cities])

  // Generate next 8 days
  useEffect(() => {
    const dates = [];
    for (let i = 0; i < 8; i++) {
      dates.push(moment().add(i, 'days'));
    }
    setVisibleDates(dates);
  }, []);

  useEffect(() => {    
    if (aggregatedActivities.duration) {
      (async()=>{
        const res = await getBookingSlots(shortHash, aggregatedActivities.duration, convertLocationToPosition(customerAddress))
        setAvailableHours(res.data.availableHours);
      })()
    
    }
  }, [aggregatedActivities, shortHash, customerAddress]);

  const handleActivityChange = (value) => {
    setSelectedServices(value);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const handleTabChange = (value) => {
    setSelectedTab(value)
    setSelectedServices(null)
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const navigateDates = (direction) => {
    const lastDate = visibleDates[visibleDates.length - 1];
    const newDates = [];
    if (direction === 'next') {
      for (let i = 1; i <= 7; i++) {
        newDates.push(moment(lastDate).add(i, 'days'));
      }
    } else {
      const firstDate = visibleDates[0];
      for (let i = 8; i >= 1; i--) {
        newDates.push(moment(firstDate).subtract(i, 'days'));
      }
    }
    setVisibleDates(direction === 'next' ? [...visibleDates.slice(7), ...newDates] : newDates);
  };

  const isBookingReady = selectedServices?.length > 0 && selectedDate && selectedTime && (!customerAddress||(customerAddress && isLocationValid));
  const selectedDateStr = useMemo(()=>selectedDate?.format('yyyy-MM-DD'),[selectedDate])
  return (
    <Box className={classes.root}>
           {(cities?.length > 1 && <SelctAppointmentLocaiton 
           isSeriveAtClinicToo={!!address}
      customerAddress={customerAddress}
      setCustomerAddress={setCustomerAddress}
      selectedTab={selectedTab}
      setSelectedTab={handleTabChange}
      isLocationValid={isLocationValid}
      />
    )}
          <FormControl variant="outlined" className={classes.activitySelect}>
          <InputLabel id="select-label">{t('select_activity')}</InputLabel>
      <Select id="select"
      multiple={allowMultipleServices}
      label={ t('select_activity')}
             variant='outlined'
        color='primary'
        className={classes.activitySelect}
        value={selectedServices}
        onChange={e=>{
          const newValue=e.target.value
          handleActivityChange(Array.isArray(newValue)?newValue:[newValue])}      }
        renderValue={()=>`${aggregatedActivities.subProfessionId.heName} - ${aggregatedActivities.duration} ${t('min')} - $${aggregatedActivities.price}`}
      >
        {activities?.map((activity) => (
          <MenuItem key={activity._id} value={activity}>
            {`${activity.subProfessionId.heName} - ${activity.duration} ${t('min')} - $${activity.price}`}
          </MenuItem>
        ))}
      </Select>
      </FormControl>

      <Box className={classes.dateCarousel}>
        <IconButton
          className={`${classes.navigationButton} ${!selectedServices.length ? 'disabled' : ''}`}
          onClick={() => navigateDates('prev')}
          disabled={!selectedServices.length}
        >
          <ArrowBackIosIcon />
        </IconButton>

        <Grid container spacing={2}>
          {visibleDates.map((date) => (
            <Grid item xs={3} key={date.toISOString()}>
              <Paper
              disabled={availableHours[date.format('YYYY-MM-DD')]?.length === 0}
                className={`${classes.dateCard} ${
                  selectedDate?.format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
                    ? classes.selectedDate
                    : ''
                } ${!selectedServices.length || !availableHours[date.format('YYYY-MM-DD')] || availableHours[date.format('YYYY-MM-DD')]?.length === 0 ? 'disabled' : ''}`}
                onClick={() => selectedServices.length && handleDateSelect(date)}
              >
                <Typography variant="subtitle1">
                  {date.format('ddd').charAt(0).toUpperCase() + date.format('ddd').slice(1)}
                </Typography>
                <Typography variant="h6">
                  {date.format('D')}
                </Typography>
                <Typography variant="subtitle2">
                  {date.format('MMM').charAt(0).toUpperCase() + date.format('MMM').slice(1)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <IconButton
          className={`${classes.navigationButton} ${!selectedServices.length ? 'disabled' : ''}`}
          onClick={() => navigateDates('next')}
          disabled={!selectedServices.length}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>

      {selectedDate && selectedServices.length && (
        <Box flex={1}>
          <Typography variant="h6" gutterBottom>
            {t('available_hours')}
          </Typography>
          <Grid container spacing={1}>
            {availableHours[selectedDateStr]?.length > 0 ? availableHours[selectedDateStr].map((time) => (
              <Grid item xs={3} key={time}>
                <Paper
                  className={`${classes.timeSlot} ${
                    selectedTime === time
                      ? classes.selectedTime
                      : ''
                  }`}
                  onClick={() => handleTimeSelect(time)}
                >
                  <Typography align="center">
                    {time}
                  </Typography>
                </Paper>
              </Grid>
            )):<Box>{t('there_are_no_appointments_this_date')}</Box>}
          </Grid>
        </Box>
      )}

      <Box display="flex" justifyContent="center" width="100%" flex={1} alignItems={'end'}>
        <Button
          variant="contained"
          color="primary"
          className={`${classes.actionButton} ${!isBookingReady ? 'disabled' : ''}`}
          disabled={!isBookingReady}
          onClick={() => {
            selectedDate.set('hour', +selectedTime.split(':')[0]);
            selectedDate.set('minute', +selectedTime.split(':')[1]);
        
            if (isBookingReady) {
              onSelect({
                selectedTime:selectedDate.toDate(),
                services: selectedServices,
                customerAddress
              });
            }
          }}
        >
          {t('book_appointment')}
        </Button>
      </Box>
      
    </Box>
  );
};

export default SelectDate;
